import SeoHead from "@/Component/Seo/SeoHead";
import BlogMonth from "@/Component/Blog/BlogMonth";
import {
  serverFetchBlogCategories,
  serverFetchBlogsList,
} from "@/services/server";
import { getCountryFromContext } from "@/utils/getCountryFromRequest";
import {
  fetchBlogListPages,
  formatMonthTitleFromSlug,
  normalizeBlogCategoriesResponse,
} from "@/utils/blogHelpers";
import { renderNotFoundPage } from "@/utils/detailSlug";
import { fetchPageSeo } from "@/utils/pageSeo";

const MIN_BLOG_SEARCH_LENGTH = 3;
const BLOG_PAGE_SIZE = 12;

export default function BlogMonthPage({
  seo,
  seoCountry,
  monthSlug,
  monthTitle,
  initialBlogList,
  initialBlogMeta,
  country,
}) {
  const fallbackTitle = monthTitle
    ? `${monthTitle} | Blog | Next Holidays`
    : "Blog | Next Holidays";

  return (
    <>
      <SeoHead seo={seo} country={seoCountry} fallbackTitle={fallbackTitle} />
      <BlogMonth
        monthSlug={monthSlug}
        monthTitle={monthTitle}
        initialBlogList={initialBlogList}
        initialBlogMeta={initialBlogMeta}
        country={country}
      />
    </>
  );
}

export async function getServerSideProps(context) {
  const country = getCountryFromContext(context);
  const seoResult = await fetchPageSeo(context);
  if (seoResult.redirect) return seoResult;

  const monthSlug = String(context.params?.slug || "")
    .trim()
    .toLowerCase();

  const validMonths = new Set([
    "january",
    "february",
    "march",
    "april",
    "may",
    "june",
    "july",
    "august",
    "september",
    "october",
    "november",
    "december",
  ]);

  if (!monthSlug || !validMonths.has(monthSlug)) {
    return renderNotFoundPage(context);
  }

  const rawSearch = String(context.query.searchText || "").trim();
  const searchText =
    rawSearch.length >= MIN_BLOG_SEARCH_LENGTH ? rawSearch : "";
  const page = Math.max(1, parseInt(context.query.page || "1", 10) || 1);
  const monthTitle = formatMonthTitleFromSlug(monthSlug);

  try {
    const [blogData, categoriesResponse] = await Promise.all([
      fetchBlogListPages(
        {
          searchText,
          pageSize: BLOG_PAGE_SIZE,
          page,
          regionTagSlug: [monthSlug],
        },
        (payload) => serverFetchBlogsList(payload, country)
      ),
      serverFetchBlogCategories(country),
    ]);

    return {
      props: {
        seo: seoResult.seo,
        seoCountry: seoResult.seoCountry,
        monthSlug,
        monthTitle,
        initialBlogList: blogData.items,
        initialBlogMeta: {
          totalCount: blogData.totalCount,
          pageIndex: blogData.pageIndex,
          totalPages: blogData.totalPages,
          pageSize: blogData.pageSize,
          searchText: blogData.searchText,
          regionTagSlug: [monthSlug],
        },
        country,
      },
    };
  } catch (error) {
    console.error("Blog month page SSR error:", error);
    return {
      props: {
        seo: seoResult.seo,
        seoCountry: seoResult.seoCountry,
        monthSlug,
        monthTitle,
        initialBlogList: [],
        initialBlogMeta: {
          totalCount: 0,
          pageIndex: 1,
          totalPages: 1,
          pageSize: BLOG_PAGE_SIZE,
          searchText: "",
          regionTagSlug: [monthSlug],
        },
        country,
      },
    };
  }
}
